import { useState } from 'react';
import { CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUpgradeOffer } from '../../hooks/useUpgradeOffer';
import { useImpersonation } from '../../hooks/useImpersonation';
import { useCredits } from '../../context/CreditsContext';
import { UPGRADE_ERROR_CODES, useUpgradeService } from '../../services/upgradeService';
import InsufficientCreditsModal from '../vinCode/insufficientCreditsModal';
import UpgradeConfirmModal from './UpgradeConfirmModal';
import classes from './upgradeBanner.module.scss';

export const combinedReportPath = (vin, jobUuid) => `/combined-report/${vin}/${jobUuid}`;

/**
 * Above a Standard report (SC-QA-27): what the Complete report adds for this VIN, and "Upgrade for 1 credit". Shown
 * only when the API offers the upgrade for this very order; never in an impersonation tab (it would spend credits).
 */
export default function UpgradeBanner({ vin, jobUuid }) {
    const { t } = useTranslation(['upgrade']);
    const navigate = useNavigate();
    const { isImpersonating } = useImpersonation();
    const { credits, updateCredits } = useCredits() || {};
    const { upgradeToComplete } = useUpgradeService();
    const { offer } = useUpgradeOffer(vin, { enabled: !isImpersonating && !!jobUuid });

    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [isInsufficientOpen, setIsInsufficientOpen] = useState(false);
    const [isUpgrading, setIsUpgrading] = useState(false);
    const [error, setError] = useState(null);
    const [hidden, setHidden] = useState(false);

    if (hidden || isImpersonating || !offer?.canUpgrade || offer.jobUuid !== jobUuid) {
        return null;
    }

    const openUpgrade = () => {
        setError(null);
        // credits are known from the header balance; the API checks again
        if (typeof credits === 'number' && credits < offer.price) {
            setIsInsufficientOpen(true);
            return;
        }
        setIsConfirmOpen(true);
    };

    const confirmUpgrade = async () => {
        if (isUpgrading) {
            return;
        }
        setIsConfirmOpen(false);
        setIsUpgrading(true);
        try {
            const result = await upgradeToComplete(jobUuid);
            if (typeof credits === 'number') {
                updateCredits?.(credits - offer.price);
            }
            // replace: Back from the Complete report must not land on the Standard view of the same order
            navigate(combinedReportPath(result.vin || vin, result.uuid || jobUuid), { replace: true });
        } catch (requestError) {
            const code = requestError?.response?.data?.code;
            if (code === UPGRADE_ERROR_CODES.ALREADY_COMPLETE) {
                navigate(combinedReportPath(vin, jobUuid), { replace: true });
                return;
            }
            if (code === UPGRADE_ERROR_CODES.NO_INTERNAL_DATA || code === UPGRADE_ERROR_CODES.NOT_POSSIBLE) {
                setHidden(true);
                return;
            }
            if (requestError?.response?.status === 400) {
                // the spend refused: not enough credits after all
                setIsInsufficientOpen(true);
                return;
            }
            setError(t('upgrade:banner.error'));
        } finally {
            setIsUpgrading(false);
        }
    };

    return (
        <section className="print-hide" aria-labelledby="upgrade-banner-title" style={{ width: '100%' }}>
            <div className={classes.banner}>
                <div className={classes.body}>
                    <span className={classes.eyebrow}>{t('upgrade:banner.eyebrow')}</span>
                    <h2 id="upgrade-banner-title" className={classes.title}>
                        {t('upgrade:banner.title')}
                    </h2>
                    <p className={classes.intro}>{t('upgrade:banner.intro')}</p>
                    <ul className={classes.sections}>
                        {offer.sections.map(section => (
                            <li key={section}>
                                <CheckOutlined className={classes.check} aria-hidden="true" />
                                {t(`upgrade:sections.${section}`)}
                            </li>
                        ))}
                    </ul>
                    {error && (
                        <span className={classes.error} role="alert">
                            {error}
                        </span>
                    )}
                </div>
                <div className={classes.cta}>
                    <button type="button" className={classes.button} onClick={openUpgrade} disabled={isUpgrading}>
                        {isUpgrading && <LoadingOutlined aria-hidden="true" />}
                        {t('upgrade:banner.button', { count: offer.price })}
                    </button>
                    <span className={classes.note}>{t('upgrade:banner.note')}</span>
                </div>
            </div>
            <UpgradeConfirmModal
                isVisible={isConfirmOpen}
                price={offer.price}
                currentCredits={credits}
                onCancel={() => setIsConfirmOpen(false)}
                onConfirm={confirmUpgrade}
            />
            <InsufficientCreditsModal
                isVisible={isInsufficientOpen}
                onCancel={() => setIsInsufficientOpen(false)}
                onConfirm={() => navigate(`/payment?vincode=${encodeURIComponent(vin)}`)}
            />
        </section>
    );
}
